import { el, clear, fragment } from '../dom.js';
import { admin, adminRaw } from '../admin-api.js';
import { t, formatDate } from '../i18n.js';
import { atLeastRank } from '../../server/lib/ranks.js';

// The moderation queue. Deliberately plain: this job has to be small enough
// that one person does it in an hour a week, and a dashboard with charts
// would make it feel like a job instead of a chore that fits in a break.
export async function renderModeration({ state }) {
  // The ladder, not the one rank: an admin sits above a moderator and must not
  // be locked out of the queue by an equality check.
  if (!atLeastRank(state.session?.user?.role, 'moderator')) {
    return el('div', { class: 'pub-empty' }, t.moderatorOnly);
  }
  // `atLeast()` refuses this same session server-side once it holds a rank
  // above `person` and no passkey — the flag mirrors that here, or the queue
  // renders and every call behind it answers 403.
  if (state.session?.enrolling) {
    return el('div', { class: 'pub-empty' }, t.moderatorNeedsPasskey);
  }

  const body = el('div');
  await paint();

  async function paint(message) {
    const [apps, reports, log, jobs, vocabulary] = await Promise.all([
      admin('GET', '/applications'),
      admin('GET', '/reports'),
      admin('GET', '/audit?limit=25'),
      admin('GET', '/jobs'),
      admin('GET', '/taxonomy'),
    ]);

    clear(body);
    if (message) body.append(el('p', { class: 'cal-hint', role: 'status' }, message));

    body.append(
      section(t.applications, apps.length, apps.map(applicationRow)),
      section(t.reports, reports.length, reports.map(reportRow)),
      vocabularySection(vocabulary),
      jobSection(jobs),
      auditSection(log),
      flowSection(),
    );
  }

  // The vocabulary, editable. Every other screen in this file reviews what
  // somebody else wrote; this one is the only place a moderator *adds*
  // something, and it is what makes "the rows are data, not an enum" true of
  // the product rather than only of the schema.
  function vocabularySection(vocabulary) {
    const domains = vocabulary?.domains ?? [];
    const settings = vocabulary?.settings ?? [];

    const rows = [
      ...domains.flatMap((domain) => [
        facetRow(domain, 0),
        ...domain.formats.map((facet) => facetRow(facet, 1)),
        ...domain.styles.map((facet) => facetRow(facet, 1)),
      ]),
      ...settings.map((facet) => facetRow(facet, 0)),
    ];

    return el('section', { class: 'mod-section' }, [
      el('h2', { class: 'bucket-title' }, t.vocabulary),
      el('p', { class: 'card-meta' }, t.vocabularyHint),
      el('ul', { class: 'card-list' }, rows),
      addFacetForm(domains),
    ]);
  }

  function facetRow(facet, depth) {
    const kindLabel = { domain: t.kindDomain, format: t.kindFormat, style: t.kindStyle, setting: t.kindSetting };

    const remove = el('button', { class: 'button button-quiet', type: 'button' },
      facet.seriesCount > 0 ? t.vocabularyUsed(facet.seriesCount) : t.vocabularyDelete);
    // Refused rather than hidden, and the button says why by naming the count:
    // deleting a facet in use cascades through `series_taxonomy` and strips the
    // classification from every event carrying it, silently.
    remove.disabled = facet.seriesCount > 0;
    remove.title = facet.seriesCount > 0 ? t.vocabularyDeleteBlocked : '';
    remove.addEventListener('click', async () => {
      remove.disabled = true;
      const response = await adminRaw('DELETE', `/taxonomy/${facet.id}`);
      await paint(response.ok ? null : (await response.json().catch(() => ({}))).message);
    });

    return el('li', { class: 'card row', dataset: { depth: String(depth) } }, [
      el('span', {}, facet.name),
      el('span', { class: 'badge' }, kindLabel[facet.kind] ?? facet.kind),
      el('code', { class: 'card-meta' }, facet.slug),
      el('span', { class: 'row-end' }, remove),
    ]);
  }

  function addFacetForm(domains) {
    const name = el('input', { type: 'text', 'aria-label': t.vocabularyName, placeholder: t.vocabularyName });
    const slug = el('input', { type: 'text', 'aria-label': t.vocabularySlug, placeholder: t.vocabularySlug });

    const kind = el('select', { 'aria-label': t.vocabularyKind }, [
      el('option', { value: 'domain' }, t.kindDomain),
      el('option', { value: 'format' }, t.kindFormat),
      el('option', { value: 'style' }, t.kindStyle),
      el('option', { value: 'setting' }, t.kindSetting),
    ]);

    const parent = el('select', { 'aria-label': t.vocabularyParent },
      domains.map((domain) => el('option', { value: domain.slug }, domain.name)));

    // A format or style without a domain is unreachable in an editor that only
    // offers them under a chosen one, so the control follows the model rather
    // than letting the server refuse afterwards.
    const syncParent = () => { parent.hidden = kind.value !== 'format' && kind.value !== 'style'; };
    kind.addEventListener('change', syncParent);
    syncParent();

    const error = el('p', { class: 'pub-error', role: 'alert' });
    error.hidden = true;

    const submit = el('button', { class: 'button', type: 'submit' }, t.vocabularyAdd);
    const form = el('form', { class: 'row mod-add' }, [name, slug, kind, parent, submit, error]);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      error.hidden = true;
      submit.disabled = true;

      const response = await adminRaw('POST', '/taxonomy', {
        kind: kind.value,
        parentSlug: parent.hidden ? null : parent.value,
        name: name.value.trim(),
        slug: slug.value.trim() || null,
      });

      if (response.ok) return paint();
      const body = await response.json().catch(() => ({}));
      error.textContent = body.message ?? body.error ?? '';
      error.hidden = false;
      submit.disabled = false;
      return null;
    });

    return form;
  }

  function section(title, count, rows) {
    return el('section', { class: 'mod-section' }, [
      el('h2', { class: 'bucket-title' }, `${title} (${count})`),
      rows.length === 0
        ? el('p', { class: 'pub-empty' }, t.nothingPending)
        : el('ul', { class: 'card-list' }, rows),
    ]);
  }

  function applicationRow(app) {
    const note = el('input', { type: 'text', placeholder: t.reviewNote });
    const approve = el('button', { class: 'button', type: 'button' }, t.approve);
    const reject = el('button', { class: 'button button-quiet', type: 'button' }, t.reject);

    approve.addEventListener('click', async () => {
      approve.disabled = true;
      const result = await admin('POST', `/applications/${app.id}/approve`, { note: note.value });
      await paint(result?.venue ? `${t.approved} tba.tips/${result.venue.slug}` : t.actionFailed);
    });

    reject.addEventListener('click', async () => {
      if (!window.confirm(t.confirmReject)) return;
      reject.disabled = true;
      await admin('POST', `/applications/${app.id}/reject`, { note: note.value });
      await paint(t.rejected);
    });

    return el('li', {}, el('div', { class: 'card' }, [
      el('h3', { class: 'card-title' }, app.venueName),
      el('p', { class: 'card-meta' },
        [app.cityName, app.address, app.website].filter(Boolean).join(' · ')),
      el('p', { class: 'card-meta' }, `${app.applicant.email} · ${app.contact ?? '—'}`),
      app.note ? el('p', {}, app.note) : null,
      el('div', { class: 'row' }, [note, approve, reject]),
    ].filter(Boolean)));
  }

  // One row, three decisions and a mandatory reason. The deadline is on the
  // row rather than in a report nobody opens: the window is promised on a
  // public page, so the queue that has to keep it is where it belongs.
  function reportRow(report) {
    const reason = el('input', {
      type: 'text',
      'aria-label': t.reportReasonGiven,
      placeholder: t.reportReasonGiven,
    });

    const error = el('p', { class: 'pub-error', role: 'alert' });
    error.hidden = true;

    const act = (action, label, quiet) => {
      const button = el('button', { class: `button${quiet ? ' button-quiet' : ''}`, type: 'button' }, label);
      // A ban needs somebody to ban. Disabled and labelled rather than absent,
      // because a button that vanishes reads as a bug and one that 422s reads
      // as a broken backend — this reads as what it is.
      if (action === 'ban' && !report.subjectUserId) {
        button.disabled = true;
        button.title = t.reportNoAccount;
      }
      button.addEventListener('click', async () => {
        error.hidden = true;
        if (!reason.value.trim()) {
          error.textContent = t.reportNeedsReason;
          error.hidden = false;
          reason.focus();
          return;
        }
        button.disabled = true;
        const response = await adminRaw('POST', `/reports/${report.id}/resolve`, {
          action, reason: reason.value.trim(),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          error.textContent = body.error === 'no_account_behind_it' ? t.reportNoAccount : t.actionFailed;
          error.hidden = false;
          button.disabled = false;
          return;
        }
        const body = await response.json();
        await paint(body.unpublishedSeries !== undefined
          ? t.reportUnpublished(body.unpublishedSeries)
          : t.reportResolved);
      });
      return button;
    };

    const erasure = report.deadline === 'erasure';

    return el('li', {}, el('div', { class: 'card' }, [
      el('div', { class: 'row' }, [
        el('h3', { class: 'card-title' },
          report.target?.title ?? `${report.targetType} #${report.targetId}`),
        // Overdue against the promise, not against a feeling. Erasure requests
        // carry their own clock and their own label — measuring an Art. 17
        // request against three working days would cry wolf, and a queue that
        // does that stops being read.
        report.overdue
          ? el('span', { class: 'badge badge-draft row-end' }, t.reportOverdue)
          : el('span', { class: 'badge row-end' }, t.reportDue(report.dueAt?.slice(0, 10) ?? '')),
      ]),
      el('p', { class: 'card-meta' }, [
        erasure ? t.reportDeadlineErasure : report.reason,
        ` · ${t.reportAge(report.ageHours)}`,
        ` · ${report.reporter ?? t.anonymous}`,
      ].join('')),
      report.detail ? el('p', {}, report.detail) : null,
      // Somebody who filed without an account has no other way back. Shown on
      // the row so the answer is written while the decision is being taken,
      // not looked up afterwards and forgotten.
      erasure
        ? el('p', { class: 'card-meta' },
            report.contact ? t.reportContactBack(report.contact) : t.reportNoContact)
        : null,
      el('div', { class: 'row' }, [reason]),
      el('div', { class: 'row row-tight' }, [
        act('unpublish', t.reportUnpublish, false),
        act('ban', t.reportBan, true),
        act('dismiss', t.reportDismiss, true),
      ]),
      el('p', { class: 'card-meta' }, t.reportTargetHint),
      error,
    ].filter(Boolean)));
  }

  // Silent jobs are bugs. If the materialiser stalls, the calendar quietly
  // stops growing and nothing else would say so.
  function jobSection(jobs) {
    return el('section', { class: 'mod-section' }, [
      el('h2', { class: 'bucket-title' }, t.jobRuns),
      jobs.length === 0
        ? el('p', { class: 'pub-empty' }, t.noJobRuns)
        : el('ul', { class: 'card-list' }, jobs.slice(0, 5).map((job) => el('li', {},
            el('p', { class: 'card-meta' },
              `${job.name} · ${job.status} · ${job.started_at}${job.error ? ` · ${job.error}` : ''}`)))),
    ]);
  }

  // The only link into /intern/datenfluss anywhere in the product, and it is
  // here rather than in the footer on purpose: the page is unlisted, and a
  // page reachable from one place is a page that stays that way. Last in the
  // column because it is something to look up, not something to work through —
  // the queue above it is the job.
  //
  // A plain anchor, not a route: it leaves the app shell for a server-rendered
  // page, which is what it is.
  function flowSection() {
    return el('section', { class: 'mod-section' }, [
      el('h2', { class: 'bucket-title' },
        el('a', { href: '/intern/datenfluss' }, t.flowTitle)),
      el('p', { class: 'card-meta' }, t.flowAdminHint),
    ]);
  }

  function auditSection(log) {
    return el('section', { class: 'mod-section' }, [
      el('h2', { class: 'bucket-title' }, t.auditLog),
      el('ul', { class: 'card-list' }, log.map((row) => el('li', {},
        el('p', { class: 'card-meta' },
          `${row.createdAt.slice(0, 16).replace('T', ' ')} · ${row.actor} · ${row.action}` +
          (row.targetId ? ` · ${row.targetType} #${row.targetId}` : ''))))),
    ]);
  }

  return fragment([
    el('h1', { class: 'pub-title' }, t.moderation),
    el('p', { class: 'pub-lede' }, t.moderationLede),
    body,
  ]);
}
